import { ReportResource, ReportRunResource, StarResource } from '@mode/legacy/data-access-model-js';
import { RecentReportRun } from '@mode/legacy/types-app-state';
import { ApiTypes } from '@mode/shared/contract-common';
import { ReportActionType } from '@mode/legacy/util-domain';

export { ReportActionType };

export enum ReportUpdateStrategy {
  Silent = 'Silent',
  SilentAndSelectNewV2Chart = 'SilentAndSelectNewV2Chart',
  SilentAndRefreshPreview = 'SilentAndRefreshPreview',
  SilentAndSync = 'SilentAndSync',
  AlertAndRefreshPreview = 'AlertAndRefreshPreview',
}
export interface RenameReportAction {
  type: ReportActionType.RenameReport;
  payload: {
    token: string;
    name: string;
    description: string;
  };
}

export interface UpdateReportAction {
  type: ReportActionType.UpdateReport;
  payload: {
    token: string;
    strategy: ReportUpdateStrategy;
    commitMessage?: string;
  };
}

export interface UpdateReportSuccessAction {
  type: ReportActionType.UpdateReportSuccess;
  payload: {
    report: ReportResource;
    commitMessage?: string;
    strategy: ReportUpdateStrategy;
  };

  isNew?: boolean; // carried through from ReportRunActionType.ExecuteReportRunSuccess
}

export interface UpdateReportFailureAction {
  type: ReportActionType.UpdateReportFailure;
  payload: {
    error: Error;
    strategy: ReportUpdateStrategy;
  };
}

export interface ReportLayoutTextChangedAction {
  type: ReportActionType.ReportLayoutTextChanged;
  layout: string;
}

export interface UpdateSelectedReportSpaceAction {
  type: ReportActionType.UpdateSelectedReportSpace;
  payload: {
    space: any;
  };
}

export interface UpdateSelectedReportSpaceSuccessAction {
  type: ReportActionType.UpdateSelectedReportSpaceSuccess;
  payload: {
    space: any;
    report: ReportResource;
    isViaPublishAction?: boolean;
    switchViewToken?: string;
  };
}

export interface UpdateSelectedReportSpaceFailureAction {
  type: ReportActionType.UpdateSelectedReportSpaceFailure;
  payload: {
    error: Error;
  };
}

export interface UpdateIfReportNeedsUpdateAction {
  type: ReportActionType.UpdateIfReportNeedsUpdate;
}

export interface UpdateSelectedReportLastSavedAtAction {
  type: ReportActionType.UpdateSelectedReportLastSavedAt;
}

export interface SaveReportChartsAction {
  type: ReportActionType.SaveReportCharts;
  payload: {
    token: string;
    needsUpdate: boolean;
    newChart: boolean;
  };
}

export interface SaveReportChartsSuccessAction {
  type: ReportActionType.SaveReportChartsSuccess;
  payload: {};
}

export interface SaveReportChartsFailureAction {
  type: ReportActionType.SaveReportChartsFailure;
  payload: {};
}

export interface SaveSingleReportChartSuccessAction {
  type: ReportActionType.SaveSingleReportChartSuccess;
  payload: {
    chart: any;
    needsUpdate: boolean;
    newChart: boolean;
  };
}

export interface SaveSingleReportChartFailureAction {
  type: ReportActionType.SaveSingleReportChartFailure;
  payload: { error };
}

export interface SaveReportTablesAction {
  type: ReportActionType.SaveReportTables;
  payload: {
    token: string;
    needsUpdate: boolean;
  };
}

export interface SaveReportTablesSuccessAction {
  type: ReportActionType.SaveReportTablesSuccess;
  payload: {};
}

export interface SaveReportTablesFailureAction {
  type: ReportActionType.SaveReportTablesFailure;
  payload: {};
}

export interface SaveSingleReportTableSuccessAction {
  type: ReportActionType.SaveSingleReportTableSuccess;
  payload: {
    table: any;
    needsUpdate: boolean;
  };
}

export interface LoadEmbeddedReportAction {
  type: ReportActionType.LoadEmbeddedReport;
  resource: ReportRunResource;
}

export interface UpdateEmbeddedReportAction {
  type: ReportActionType.UpdateEmbeddedReport;
  resource: ApiTypes.ModeResource;
  isNew: boolean; // has to carry forward from ReportRunActionType.ExecuteReportRunSuccess
}

export interface SaveSingleReportTableFailureAction {
  type: ReportActionType.SaveSingleReportTableFailure;
  payload: { error };
}

export interface SetSelectedReportEmbedded {
  type: ReportActionType.SetSelectedReportEmbedded;
  payload: {
    value: boolean;
  };
}

export interface SetSelectedReportEmbeddedSuccess {
  type: ReportActionType.SetSelectedReportEmbeddedSuccess;
  payload: {
    reportResource: ReportResource;
  };
}

export interface SetSelectedReportEmbeddedFailure {
  type: ReportActionType.SetSelectedReportEmbeddedFailure;
  payload: { error };
}

export interface SetSelectedReportShared {
  type: ReportActionType.SetSelectedReportShared;
  payload: {
    value: boolean;
  };
}

export interface SetSelectedReportSharedSuccess {
  type: ReportActionType.SetSelectedReportSharedSuccess;
  payload: {
    reportResource: ReportResource;
  };
}

export interface SetSelectedReportSharedFailure {
  type: ReportActionType.SetSelectedReportSharedFailure;
  payload: { error };
}

export interface ToggleReportUserStarAction {
  type: ReportActionType.ToggleReportUserStar;
}

export interface ToggleReportUserStarSuccessAction {
  type: ReportActionType.ToggleReportUserStarSuccess;
  starResource: StarResource;
}

export interface ToggleReportUserStarFailureAction {
  type: ReportActionType.ToggleReportUserStarFailure;
  error: Error;
}

export interface UpdateRecentReportAction {
  type: ReportActionType.UpdateRecentReport;
  payload: {
    hasRecentReportRun: boolean;
  };
}

export interface UpdateRecentReportDetailsAction {
  type: ReportActionType.UpdateRecentReportDetails;
  payload: {
    recentReportRun: RecentReportRun;
  };
}

export class LoadReportThemeSuccess {
  type: ReportActionType.LoadThemeSuccess;
  constructor(
    public readonly href: string,
    public readonly name: string,
    public readonly token: string,
    public readonly cssSource: string
  ) {}
}

export interface InitQueryResultPagination {
  type: ReportActionType.InitQueryResultPagination;
  token: string;
}

export interface UpdateReportSettings {
  type: ReportActionType.UpdateReportSettings;
  payload: {
    full_width?: boolean;
    theme?: number;
  };
}

export interface SetReportCaptureCreatedTime {
  type: ReportActionType.SetReportCaptureRunCreatedTime;
  createdAt: Date | null;
}

export interface PublishDatasetAfterMoveSpaceAction {
  type: ReportActionType.PublishDatasetAfterMoveSpace;
  switchViewToken: string;
}

export type ReportActions =
  | LoadEmbeddedReportAction
  | LoadReportThemeSuccess
  | UpdateEmbeddedReportAction
  | RenameReportAction
  | UpdateReportAction
  | UpdateReportFailureAction
  | UpdateReportSuccessAction
  | ReportLayoutTextChangedAction
  | UpdateSelectedReportSpaceAction
  | UpdateSelectedReportSpaceSuccessAction
  | UpdateSelectedReportSpaceFailureAction
  | UpdateIfReportNeedsUpdateAction
  | UpdateSelectedReportLastSavedAtAction
  | SaveReportTablesAction
  | SaveReportTablesSuccessAction
  | SaveReportTablesFailureAction
  | SaveSingleReportTableSuccessAction
  | SaveSingleReportTableFailureAction
  | SaveReportChartsAction
  | SaveReportChartsSuccessAction
  | SaveReportChartsFailureAction
  | SaveSingleReportChartSuccessAction
  | SaveSingleReportChartFailureAction
  | SetSelectedReportEmbedded
  | SetSelectedReportEmbeddedSuccess
  | SetSelectedReportEmbeddedFailure
  | SetSelectedReportShared
  | SetSelectedReportSharedSuccess
  | SetSelectedReportSharedFailure
  | ToggleReportUserStarAction
  | ToggleReportUserStarSuccessAction
  | ToggleReportUserStarFailureAction
  | UpdateRecentReportAction
  | UpdateRecentReportDetailsAction
  | InitQueryResultPagination
  | UpdateReportSettings
  | SetReportCaptureCreatedTime
  | PublishDatasetAfterMoveSpaceAction;
